package org.example.project.engineslot

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.encodeToString
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.buildJsonObject
import org.example.project.settings.AppDropdown
import org.example.project.settings.AppDropdownAppearance
import org.example.project.settings.AppDropdownMenuMode
import org.example.project.settings.LumaticaButton
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val SLOT_EXTRA_LADEN_DRIVER_ID = "slotDriverUserId"
private const val SLOT_EXTRA_EMPFAENGER_FIRMEN_ID = "slotEmpfaengerFirmenId"
private const val SLOT_EXTRA_AUFTRAGGEBER_FIRMEN_ID = "slotAuftraggeberFirmenId"
private const val SLOT_EXTRA_LADEN_UNTERNEHMER_FIRMEN_ID = "slotUnternehmerFirmenId"
const val SLOT_EXTRA_LADEN_TOUREN = "slotLadenTouren"
private const val SLOT_LADEN_CARD_WIDTH_PX = 760
private const val SLOT_LADEN_INLINE_LABEL_WIDTH_PX = 176

private val slotLadenJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun slotLadenCompactTour(tour: All): All =
    All(
        id = tour.id,
        lieferscheinnummer = tour.lieferscheinnummer,
        palettenanzahl = 0,
        lieferant = tour.lieferant,
        empfaenger = tour.empfaenger,
        auftraggeber = tour.auftraggeber,
        fahrername = tour.fahrername,
        lkwkennzeichen = tour.lkwkennzeichen,
        aufliegerkennzeichen = tour.aufliegerkennzeichen,
        unternehmer = tour.unternehmer,
        ort = tour.ort,
        zustand = tour.zustand,
        slotDriverUserId = tour.slotDriverUserId,
        lieferantFirmenId = tour.lieferantFirmenId,
        empfaengerFirmenId = tour.empfaengerFirmenId,
        auftraggeberFirmenId = tour.auftraggeberFirmenId,
        unternehmerFirmenId = tour.unternehmerFirmenId,
        endToEndFlow = tour.endToEndFlow,
        flowTyp = tour.flowTyp,
        flowStatus = tour.flowStatus,
        flowId = tour.flowId,
        flowTargetFirmenId = tour.flowTargetFirmenId,
        flowTargetWeId = tour.flowTargetWeId,
        flowTargetWeLieferscheinnummer = tour.flowTargetWeLieferscheinnummer
    )

private fun slotLadenEncodeTouren(touren: List<All>): String =
    slotLadenJson.encodeToString(
        JsonArray(
            touren.map { tour ->
                buildJsonObject {
                    if (tour.id.isNotBlank()) put("id", JsonPrimitive(tour.id))
                    if (tour.lieferscheinnummer.isNotBlank()) put("lieferscheinnummer", JsonPrimitive(tour.lieferscheinnummer))
                    put("palettenanzahl", JsonPrimitive(tour.palettenanzahl.coerceAtLeast(0)))
                    if (tour.lieferant.isNotBlank()) put("lieferant", JsonPrimitive(tour.lieferant))
                    if (tour.empfaenger.isNotBlank()) put("empfaenger", JsonPrimitive(tour.empfaenger))
                    if (tour.auftraggeber.isNotBlank()) put("auftraggeber", JsonPrimitive(tour.auftraggeber))
                    if (tour.fahrername.isNotBlank()) put("fahrername", JsonPrimitive(tour.fahrername))
                    if (tour.lkwkennzeichen.isNotBlank()) put("lkwkennzeichen", JsonPrimitive(tour.lkwkennzeichen))
                    if (tour.aufliegerkennzeichen.isNotBlank()) put("aufliegerkennzeichen", JsonPrimitive(tour.aufliegerkennzeichen))
                    if (tour.unternehmer.isNotBlank()) put("unternehmer", JsonPrimitive(tour.unternehmer))
                    if (tour.ort.isNotBlank()) put("ort", JsonPrimitive(tour.ort))
                    if (tour.zustand != 0) put("zustand", JsonPrimitive(tour.zustand))
                    if (tour.slotDriverUserId.isNotBlank()) put("slotDriverUserId", JsonPrimitive(tour.slotDriverUserId))
                    if (tour.lieferantFirmenId.isNotBlank()) put("lieferantFirmenId", JsonPrimitive(tour.lieferantFirmenId))
                    if (tour.empfaengerFirmenId.isNotBlank()) put("empfaengerFirmenId", JsonPrimitive(tour.empfaengerFirmenId))
                    if (tour.auftraggeberFirmenId.isNotBlank()) put("auftraggeberFirmenId", JsonPrimitive(tour.auftraggeberFirmenId))
                    if (tour.unternehmerFirmenId.isNotBlank()) put("unternehmerFirmenId", JsonPrimitive(tour.unternehmerFirmenId))
                    if (tour.endToEndFlow) put("endToEndFlow", JsonPrimitive(true))
                    if (tour.flowTyp.isNotBlank()) put("flowTyp", JsonPrimitive(tour.flowTyp))
                    if (tour.flowStatus.isNotBlank()) put("flowStatus", JsonPrimitive(tour.flowStatus))
                    if (tour.flowId.isNotBlank()) put("flowId", JsonPrimitive(tour.flowId))
                    if (tour.flowTargetFirmenId.isNotBlank()) put("flowTargetFirmenId", JsonPrimitive(tour.flowTargetFirmenId))
                    if (tour.flowTargetWeId.isNotBlank()) put("flowTargetWeId", JsonPrimitive(tour.flowTargetWeId))
                    if (tour.flowTargetWeLieferscheinnummer.isNotBlank()) put("flowTargetWeLieferscheinnummer", JsonPrimitive(tour.flowTargetWeLieferscheinnummer))
                }
            }
        )
    )

@Composable
fun SlotBookingLadenContent(
    state: All,
    masterData: List<All>,
    standorte: List<All>,
    zugmaschinen: List<All>,
    auflieger: List<All>,
    hasMultipleStandorte: Boolean,
    selectedAbholort: String,
    selectedAbholortLabel: String,
    readOnly: Boolean = false,
    onStateChange: (All) -> Unit
) {
    var users by remember { mutableStateOf<List<All>>(emptyList()) }
    var roles by remember { mutableStateOf<List<All>>(emptyList()) }
    var slotBookings by remember { mutableStateOf<List<All>>(emptyList()) }
    var driverSchedulePopup by remember { mutableStateOf<List<String>?>(null) }

    LaunchedEffect(Unit) {
        users = loadSlotCompanyUsers()
        roles = loadSlotVisibleRoles()
    }

    LaunchedEffect(state.firmenId, state.datum) {
        slotBookings = runCatching { loadSlotDriverScheduleBookings(state.firmenId) }.getOrDefault(emptyList())
    }

    val lieferantOptions = remember(masterData) { slotLadenAddressDropdownOptions(masterData, SLOT_MASTER_EMPFAENGER) }
    val empfaengerOptions = remember(masterData) { slotLadenAddressDropdownOptions(masterData, SLOT_MASTER_EMPFAENGER) }
    val auftraggeberOptions = remember(masterData) { slotLadenAddressDropdownOptions(masterData, SLOT_MASTER_AUFTRAGGEBER) }
    val unternehmerOptions = remember(masterData) { slotLadenAddressDropdownOptions(masterData, SLOT_MASTER_LADEFIRMA) }

    val driverRoleIds = remember(roles) {
        roles.filter { role ->
            role.name.ifBlank { role.bezeichnung }.contains("fahrer", ignoreCase = true) ||
                role.id.equals("fahrer", ignoreCase = true)
        }.map { it.id }.toSet()
    }
    val driverUsers = remember(users, driverRoleIds) {
        users.filter { user ->
            val rolleId = user.rolle.trim()
            rolleId.isNotBlank() && (
                rolleId in driverRoleIds || rolleId.equals("fahrer", ignoreCase = true)
            )
        }.sortedBy {
            listOf(it.vorname, it.nachname).filter { part -> part.isNotBlank() }.joinToString(" ").lowercase()
        }
    }
    val driverOptions = remember(driverUsers, slotBookings, state.datum) {
        listOf(All(key = "", label = "Keine Auswahl")) + driverUsers.map { user ->
            val name = slotDriverDisplayName(user)
            val count = slotDriverDayScheduleEntries(slotBookings, user.id, state.datum).size
            All(
                key = user.id,
                label = slotDriverOptionLabel(name, count)
            )
        }
    }
    val touren = remember(state) { slotLadenTouren(state) }

    fun pushTouren(updatedTouren: List<All>) {
        val sharedDriverId = updatedTouren.firstNotNullOfOrNull { it.slotDriverUserId.takeIf(String::isNotBlank) }.orEmpty()
        val sharedFahrer = updatedTouren.firstNotNullOfOrNull { it.fahrername.takeIf(String::isNotBlank) }.orEmpty()
        val sharedLkw = updatedTouren.firstNotNullOfOrNull { it.lkwkennzeichen.takeIf(String::isNotBlank) }.orEmpty()
        val sharedAuflieger = updatedTouren.firstNotNullOfOrNull { it.aufliegerkennzeichen.takeIf(String::isNotBlank) }.orEmpty()
        val firstLieferantId = updatedTouren.firstNotNullOfOrNull { it.lieferantFirmenId.takeIf(String::isNotBlank) }.orEmpty()
        val firstEmpfaengerId = updatedTouren.firstNotNullOfOrNull { it.empfaengerFirmenId.takeIf(String::isNotBlank) }.orEmpty()
        val firstAuftraggeberId = updatedTouren.firstNotNullOfOrNull { it.auftraggeberFirmenId.takeIf(String::isNotBlank) }.orEmpty()
        val firstUnternehmerId = updatedTouren.firstNotNullOfOrNull { it.unternehmerFirmenId.takeIf(String::isNotBlank) }.orEmpty()
        val normalized = updatedTouren
            .ifEmpty { listOf(slotLadenDefaultTour(state)) }
            .map { tour ->
                if (updatedTouren.size <= 1) tour else tour.copy(
                    fahrername = sharedFahrer,
                    lkwkennzeichen = sharedLkw,
                    aufliegerkennzeichen = sharedAuflieger,
                    slotDriverUserId = sharedDriverId
                )
            }
            .map(::slotLadenCompactTour)
        val first = normalized.first()
        onStateChange(
            state.copy(
                lieferscheinnummer = first.lieferscheinnummer,
                palettenanzahl = 0,
                lieferant = first.lieferant,
                empfaenger = first.empfaenger,
                auftraggeber = first.auftraggeber,
                fahrername = first.fahrername,
                lkwkennzeichen = first.lkwkennzeichen,
                aufliegerkennzeichen = first.aufliegerkennzeichen,
                unternehmer = first.unternehmer,
                ort = first.ort,
                slotLadenTouren = slotLadenEncodeTouren(normalized),
                slotDriverUserId = first.slotDriverUserId,
                lieferantFirmenId = first.lieferantFirmenId.ifBlank { firstLieferantId },
                empfaengerFirmenId = first.empfaengerFirmenId.ifBlank { firstEmpfaengerId },
                auftraggeberFirmenId = first.auftraggeberFirmenId.ifBlank { firstAuftraggeberId },
                unternehmerFirmenId = first.unternehmerFirmenId.ifBlank { firstUnternehmerId }
            )
        )
    }

    val tourSummary = if (touren.size > 1) {
        "Sammelabholung · ${touren.size} Lieferscheine · " + touren.joinToString("  |  ") { tour ->
            tour.lieferscheinnummer.ifBlank { "-" }
        }
    } else {
        touren.joinToString("  |  ") { tour ->
            tour.lieferscheinnummer.ifBlank { "-" }
        }
    }

    Div({
        style {
            width(100.percent)
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            gap(18.px)
            padding(0.px, 0.px, 30.px, 0.px)
            boxSizing("border-box")
            overflow("hidden")
        }
    }) {
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                minHeight(44.px)
                marginBottom(30.px)
            }
        }) {
            Span({
                style {
                    color(Color.white)
                    fontSize(18.px)
                    fontWeight("700")
                    textAlign("center")
                }
            }) { Text(tourSummary) }
        }

        Div({
            style {
                flex(1)
                overflowX("auto")
                overflowY("auto")
                padding(0.px, 0.px, 24.px, 0.px)
            }
        }) {
            Div({
                style {
                    minWidth(100.percent)
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    paddingBottom(18.px)
                    boxSizing("border-box")
                }
            }) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.FlexStart)
                        gap(32.px)
                        property("width", "max-content")
                    }
                }) {
                    touren.forEachIndexed { tourIndex, tour ->
                        val selectedDriverId = tour.slotDriverUserId
                        val lkwOptions = remember(zugmaschinen, tour.lkwkennzeichen) {
                            slotVehicleDropdownOptions(zugmaschinen, tour.lkwkennzeichen)
                        }
                        val aufliegerOptions = remember(auflieger, tour.aufliegerkennzeichen) {
                            slotVehicleDropdownOptions(auflieger, tour.aufliegerkennzeichen, showAufliegerMerkmale = true)
                        }

                        Div({
                            style {
                                width(SLOT_LADEN_CARD_WIDTH_PX.px)
                                minHeight(360.px)
                                display(DisplayStyle.Flex)
                                flexDirection(FlexDirection.Column)
                                gap(18.px)
                                padding(24.px, 28.px, 28.px, 28.px)
                                boxSizing("border-box")
                                backgroundColor(Color("rgba(255,255,255,0.025)"))
                                property("border", "1px solid rgba(255,255,255,0.08)")
                                borderRadius(16.px)
                                property("box-shadow", "0 10px 24px rgba(0,0,0,0.12)")
                                flexShrink(0)
                                position(Position.Relative)
                            }
                        }) {
                            if (!readOnly && touren.size > 1) {
                                Div({
                                    style {
                                        position(Position.Absolute)
                                        top(10.px)
                                        right(10.px)
                                    }
                                }) {
                                    LumaticaButton(
                                        label = "",
                                        onClick = { pushTouren(touren.filterIndexed { index, _ -> index != tourIndex }) },
                                        heightPx = 26,
                                        paddingH = 6
                                    ) {
                                        Img(src = "images/miniclose.svg") {
                                            style {
                                                width(12.px)
                                                height(12.px)
                                            }
                                        }
                                    }
                                }
                            }
                            Span({
                                style {
                                    color(Color.white)
                                    fontSize(16.px)
                                    fontWeight("700")
                                    textAlign("center")
                                    width(100.percent)
                                }
                            }) { Text("Referenz ${tourIndex + 1}") }

                            if (readOnly) {
                                SlotLadenReadonlyRow("lieferscheinnummer", tour.lieferscheinnummer)
                                SlotLadenReadonlyRow("Empfänger", tour.empfaenger)
                                SlotLadenReadonlyRow("Lieferant", tour.lieferant)
                                SlotLadenReadonlyRow("Auftraggeber", tour.auftraggeber)
                                SlotLadenReadonlyRow("Unternehmer", tour.unternehmer)
                                SlotLadenReadonlyRow("Fahrer", tour.fahrername)
                                SlotLadenReadonlyRow("LKW", tour.lkwkennzeichen)
                                SlotLadenReadonlyRow("Auflieger", tour.aufliegerkennzeichen)
                            } else {
                                SlotLadenInputRow("lieferscheinnummer", tour.lieferscheinnummer) { value ->
                                    pushTouren(
                                        touren.updatedAt(
                                            tourIndex,
                                            tour.copy(lieferscheinnummer = value)
                                        )
                                    )
                                }
                                SlotLadenDropdownRow("Empfänger", empfaengerOptions, slotLadenAddressSelectedKey(empfaengerOptions, tour.empfaengerFirmenId, tour.empfaenger)) { value ->
                                    val selected = empfaengerOptions.firstOrNull { it.key == value }
                                    pushTouren(touren.updatedAt(tourIndex, tour.copy(
                                        empfaenger = selected?.label.orEmpty(),
                                        ort = "",
                                        empfaengerFirmenId = selected?.firmenId.orEmpty()
                                    )))
                                }
                                SlotLadenDropdownRow("Lieferant", lieferantOptions, slotLadenAddressSelectedKey(lieferantOptions, tour.lieferantFirmenId, tour.lieferant)) { value ->
                                    val selected = lieferantOptions.firstOrNull { it.key == value }
                                    pushTouren(touren.updatedAt(tourIndex, tour.copy(
                                        lieferant = selected?.label.orEmpty(),
                                        lieferantFirmenId = selected?.firmenId.orEmpty()
                                    )))
                                }
                                SlotLadenDropdownRow("Auftraggeber", auftraggeberOptions, slotLadenAddressSelectedKey(auftraggeberOptions, tour.auftraggeberFirmenId, tour.auftraggeber)) { value ->
                                    val selected = auftraggeberOptions.firstOrNull { it.key == value }
                                    pushTouren(touren.updatedAt(tourIndex, tour.copy(
                                        auftraggeber = selected?.label.orEmpty(),
                                        auftraggeberFirmenId = selected?.firmenId.orEmpty()
                                    )))
                                }
                                SlotLadenDropdownRow("Unternehmer", unternehmerOptions, slotLadenAddressSelectedKey(unternehmerOptions, tour.unternehmerFirmenId, tour.unternehmer)) { value ->
                                    val selected = unternehmerOptions.firstOrNull { it.key == value }
                                    pushTouren(touren.updatedAt(tourIndex, tour.copy(
                                        unternehmer = selected?.label.orEmpty(),
                                        unternehmerFirmenId = selected?.firmenId.orEmpty()
                                    )))
                                }
                                SlotLadenDropdownRow("Fahrer", driverOptions, selectedDriverId) { driverId ->
                                    val driver = driverUsers.firstOrNull { it.id == driverId }
                                    val driverName = driver?.let(::slotDriverDisplayName).orEmpty()
                                    val withDriver = { current: All ->
                                        current.copy(
                                            fahrername = driverName,
                                            lkwkennzeichen = driver?.lkwkennzeichen.orEmpty(),
                                            aufliegerkennzeichen = driver?.aufliegerkennzeichen.orEmpty(),
                                            slotDriverUserId = driverId
                                        )
                                    }
                                    pushTouren(
                                        if (touren.size > 1) touren.map(withDriver)
                                        else touren.updatedAt(tourIndex, withDriver(tour))
                                    )
                                    val schedule = slotDriverDayScheduleEntries(slotBookings, driverId, state.datum)
                                    if (schedule.isNotEmpty()) driverSchedulePopup = schedule
                                }
                                SlotLadenDropdownRow("LKW", lkwOptions, tour.lkwkennzeichen) { value ->
                                    pushTouren(
                                        if (touren.size > 1) touren.map { it.copy(lkwkennzeichen = value) }
                                        else touren.updatedAt(tourIndex, tour.copy(lkwkennzeichen = value))
                                    )
                                }
                                SlotLadenDropdownRow("Auflieger", aufliegerOptions, tour.aufliegerkennzeichen) { value ->
                                    pushTouren(
                                        if (touren.size > 1) touren.map { it.copy(aufliegerkennzeichen = value) }
                                        else touren.updatedAt(tourIndex, tour.copy(aufliegerkennzeichen = value))
                                    )
            }
        }
    }

    driverSchedulePopup?.let { entries ->
        SlotDriverSchedulePopup(
            entries = entries,
            onClose = { driverSchedulePopup = null }
        )
    }
}
                }
            }
        }
    }

}

@Composable
private fun SlotLadenInputRow(
    label: String,
    value: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotLadenInlineLabel(label)
        Input(InputType.Text, attrs = {
            value(value)
            style {
                flex(1)
                minWidth(0.px)
                height(38.px)
                padding(0.px, 0.px, 6.px, 0.px)
                backgroundColor(Color.transparent)
                color(Color.white)
                fontSize(16.px)
                border(0.px)
                property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                property("outline", "none")
                boxSizing("border-box")
            }
            onInput { onChange(it.value) }
        })
    }
}

@Composable
private fun SlotLadenReadonlyRow(
    label: String,
    value: String
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotLadenInlineLabel(label)
        Div({
            style {
                flex(1)
                minHeight(38.px)
                padding(0.px, 0.px, 6.px, 0.px)
                property("border-bottom", "1px solid rgba(255,255,255,0.24)")
                color(Color.white)
                fontSize(16.px)
                lineHeight("1.35")
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                boxSizing("border-box")
            }
        }) {
            Text(value.ifBlank { "-" })
        }
    }
}

@Composable
private fun SlotLadenDropdownRow(
    label: String,
    options: List<All>,
    selectedValue: String,
    onChange: (String) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(16.px)
            minWidth(0.px)
        }
    }) {
        SlotLadenInlineLabel(label)
        AppDropdown(
            options = options,
            selectedValue = selectedValue.ifBlank { null },
            onChange = onChange,
            placeholder = "Auswahl",
            appearance = AppDropdownAppearance.Underline,
            menuMode = AppDropdownMenuMode.Overlay,
            attrs = {
                style {
                    flex(1)
                    minWidth(0.px)
                    height(38.px)
                    padding(0.px, 0.px, 6.px, 0.px)
                    fontSize(16.px)
                }
            }
        )
    }
}

@Composable
private fun SlotLadenInlineLabel(label: String) {
    Span({
        style {
            width(SLOT_LADEN_INLINE_LABEL_WIDTH_PX.px)
            property("min-width", "${SLOT_LADEN_INLINE_LABEL_WIDTH_PX}px")
            color(Color("#8a8f9a"))
            fontSize(14.px)
            fontWeight("700")
            letterSpacing(0.2.px)
            lineHeight("1.3")
        }
    }) { Text(label) }
}

fun slotLadenTouren(state: All): List<All> {
    val stored = state.slotLadenTouren
    val parsed = runCatching {
        slotLadenJson.decodeFromString(ListSerializer(All.AllNames.serializer()), stored)
    }.getOrDefault(emptyList())
    if (parsed.isNotEmpty()) return parsed.map(::slotLadenCompactTour)
    return listOf(slotLadenDefaultTour(state))
}

fun slotLadenDefaultTour(
    state: All,
    index: Int = 1,
    prefillFromState: Boolean = true
): All =
    All(
        id = "tour-$index",
        lieferscheinnummer = if (prefillFromState) state.lieferscheinnummer else "",
        palettenanzahl = 0,
        lieferant = if (prefillFromState) state.lieferant else "",
        empfaenger = if (prefillFromState) state.empfaenger else "",
        auftraggeber = if (prefillFromState) state.auftraggeber else "",
        fahrername = if (prefillFromState) state.fahrername else "",
        lkwkennzeichen = if (prefillFromState) state.lkwkennzeichen else "",
        aufliegerkennzeichen = if (prefillFromState) state.aufliegerkennzeichen else "",
        unternehmer = if (prefillFromState) state.unternehmer else "",
        ort = if (prefillFromState) state.ort else "",
        zustand = if (prefillFromState) state.zustand else 0,
        slotDriverUserId = if (prefillFromState) state.slotDriverUserId else "",
        lieferantFirmenId = if (prefillFromState) state.lieferantFirmenId else "",
        empfaengerFirmenId = if (prefillFromState) state.empfaengerFirmenId else "",
        auftraggeberFirmenId = if (prefillFromState) state.auftraggeberFirmenId else "",
        unternehmerFirmenId = if (prefillFromState) state.unternehmerFirmenId else "",
        endToEndFlow = if (prefillFromState) state.endToEndFlow else false,
        flowTyp = if (prefillFromState) state.flowTyp else "",
        flowStatus = if (prefillFromState) state.flowStatus else "",
        flowId = if (prefillFromState) state.flowId else "",
        flowTargetFirmenId = if (prefillFromState) state.flowTargetFirmenId else "",
        flowTargetWeId = if (prefillFromState) state.flowTargetWeId else "",
        flowTargetWeLieferscheinnummer = if (prefillFromState) state.flowTargetWeLieferscheinnummer else ""
    )

fun slotLadenAppendTour(state: All): All {
    val touren = slotLadenTouren(state)
    val nextTouren = touren + slotLadenDefaultTour(
        state = state,
        index = touren.size + 1,
        prefillFromState = false
    ).copy(
        fahrername = touren.firstOrNull()?.fahrername.orEmpty(),
        lkwkennzeichen = touren.firstOrNull()?.lkwkennzeichen.orEmpty(),
        aufliegerkennzeichen = touren.firstOrNull()?.aufliegerkennzeichen.orEmpty(),
        slotDriverUserId = touren.firstOrNull()?.slotDriverUserId.orEmpty(),
        lieferantFirmenId = "",
        empfaengerFirmenId = "",
        auftraggeberFirmenId = "",
        unternehmerFirmenId = ""
    )
    val normalized = nextTouren.map(::slotLadenCompactTour)
    val first = normalized.first()
    return state.copy(
        lieferscheinnummer = first.lieferscheinnummer,
        palettenanzahl = 0,
        lieferant = first.lieferant,
        empfaenger = first.empfaenger,
        auftraggeber = first.auftraggeber,
        fahrername = first.fahrername,
        lkwkennzeichen = first.lkwkennzeichen,
        aufliegerkennzeichen = first.aufliegerkennzeichen,
        unternehmer = first.unternehmer,
        ort = first.ort,
        slotLadenTouren = slotLadenEncodeTouren(normalized),
        slotDriverUserId = first.slotDriverUserId,
        lieferantFirmenId = first.lieferantFirmenId,
        empfaengerFirmenId = first.empfaengerFirmenId,
        auftraggeberFirmenId = first.auftraggeberFirmenId,
        unternehmerFirmenId = first.unternehmerFirmenId
    )
}

private fun List<All>.updatedAt(index: Int, value: All): List<All> =
    mapIndexed { currentIndex, current -> if (currentIndex == index) value else current }

private fun slotLadenAddressDropdownOptions(entries: List<All>, typ: String): List<All> =
    listOf(All(key = "", label = "Keine Auswahl")) +
        slotMasterEntries(entries, typ).map { entry ->
            val label = listOf(
                entry.firma.ifBlank { entry.name },
                listOf(entry.strasse, entry.hausnummer).filter { it.isNotBlank() }.joinToString(" "),
                listOf(entry.plz, entry.ort).filter { it.isNotBlank() }.joinToString(" ")
            ).filter { it.isNotBlank() }.joinToString(", ").ifBlank { entry.id }
            All(
                key = entry.firmenId.ifBlank { "local:${entry.id.ifBlank { label }}" },
                label = label,
                firmenId = entry.firmenId
            )
        }.distinctBy { it.label.lowercase() }

private fun slotLadenAddressSelectedKey(options: List<All>, storedKey: String, label: String): String =
    storedKey.ifBlank {
        options.firstOrNull { it.label == label }?.key.orEmpty()
    }


